import { get, post } from '@/services/http';

/**
 * Interface for a single Shopify Metafield Definition.
 */
export interface MetafieldDefinition {
  name: string;
  key: string;
  type: {
    name: string;
  };
}

/**
 * Interface for the API response containing metafield definitions.
 */
export interface MetafieldDefinitionResponse {
  metafieldDefinitions: MetafieldDefinition[];
}

/**
 * Interface for the preview data returned from the API.
 * e.g., for vendors: { vendors: [{ name: 'Vendor A', productCount: 15 }] }
 */
export interface MigrationPreview {
  vendors: Array<{
    name: string;
    productCount: number;
    conflict?: boolean;
  }>;
  // This can be expanded to include other sources like metafields
}

/**
 * Interface for the response when starting a migration job.
 */
export interface MigrationJob {
  jobId: string;
}

/**
 * Interface for the status of a migration job.
 */
export interface JobStatus {
  // 'not_found' comes from the shared job store when the Redis record has
  // expired or was cleared — the endpoint returns it with HTTP 200, and such
  // responses carry no `message`.
  status: 'pending' | 'processing' | 'completed' | 'error' | 'not_found';
  message?: string;
  phase?: string;
  progress?: number;
  summary?: {
    suppliersCreated?: number;
    suppliersUpdated?: number;
    productsLinked?: number;
    totalVendorsProcessed?: number;
  };
  results?: {
    message: string;
    summary: {
      suppliersCreated?: number;
      suppliersUpdated?: number;
      productsLinked?: number;
      totalVendorsProcessed?: number;
    }
  };
  error?: string;
}

/**
 * Interface for the configuration passed when starting a migration.
 */
export interface MigrationConfig {
  source: 'vendor' | 'metafield';
  conflictResolution: 'skip' | 'update';
  mapping?: Record<string, string>; // e.g., { supplierName: 'custom.supplier_name' }
}


/**
 * Model for handling Shopify supplier migration API calls.
 */
export class SupplierMigrationModel {

  /**
   * Fetches a preview of the data to be imported from any source.
   * @param source The source to preview ('vendor' or 'metafield').
   * @param mapping The metafield mapping (only required for 'metafield' source).
   * @returns A promise that resolves to the migration preview data.
   */
  public static async getMigrationPreview(
    source: 'vendor' | 'metafield',
    mapping?: Record<string, string>,
  ): Promise<MigrationPreview> {
    try {
      const payload = { source, mapping };
      const response = await post<MigrationPreview>('/suppliers/migration/preview', payload);
      return response;
    } catch (error) {
      console.error(`Error fetching migration preview for source "${source}":`, error);
      throw error;
    }
  }

  /**
   * Fetches the available Shopify Product Metafield definitions.
   * @returns A promise that resolves to a list of metafield definitions.
   */
  public static async getMetafieldDefinitions(): Promise<MetafieldDefinitionResponse> {
    try {
      const response = await get<MetafieldDefinitionResponse>('/suppliers/migration/metafield-definitions');
      return response;
    } catch (error) {
      console.error('Error fetching metafield definitions:', error);
      throw error;
    }
  }

  /**
   * Starts a new supplier migration job.
   * @param config The configuration for the migration job.
   * @returns A promise that resolves to an object containing the new job's ID.
   */
  public static async startMigration(config: MigrationConfig): Promise<MigrationJob> {
    try {
      const response = await post<MigrationJob>('/suppliers/migration/import', config);
      return response;
    } catch (error) {
      console.error('Error starting supplier migration:', error);
      throw error;
    }
  }

  /**
   * Fetches the status of a specific migration job.
   * @param jobId The ID of the migration job.
   * @returns A promise that resolves to the job's status.
   */
  public static async getMigrationStatus(jobId: string): Promise<JobStatus> {
    try {
      const response = await get<JobStatus>(`/suppliers/migration/status/${jobId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching status for migration job ${jobId}:`, error);
      throw error;
    }
  }
}
